// League tables page. One table that ranks a metadata dimension
// (department, location, city, country, company, state) by a
// chosen score driver (the five risk factors). Two dropdowns drive it:
// "Score driver" picks which factor to rank by, "Group by" picks the
// metadata dimension. Clicking a row drills the shell into Sims scoped to
// that value.
//
// Built on the shared DataTable so sorting, search, the column picker,
// CSV/JSON export and pagination behave exactly like every other table in
// the dashboard.
//
// Data: the server 'leagues' aggregation, the mean of each scored user's 0-1
// factor component within every group value, and 'risk_benchmark' for the
// platform / industry anchors behind the RAG colours. No browser-model
// fallback (BD-1245): with no server rows the table is empty.
// formatFactorValue renders values in the factor's own unit (percent / days).
import React, { useMemo, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion } from 'motion/react';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { SIGNALS, formatFactorValue } from '../riskScore';
import useDashboardData from '../hooks/useDashboardData';
import { useRiskBenchmarkQuery } from './summary/BenchmarkPositionCard';
import DataTable from './DataTable';
import WidgetState from './WidgetState';
import { pluralLabel } from '../helpers';

// Metadata dimensions, mirrors MetaRiskLeagueCard order.
const DIMENSIONS = [
    { id: 'department', label: 'Department', field: 'dept',     drillType: 'department' },
    { id: 'location',   label: 'Location',   field: 'location', drillType: 'location' },
    { id: 'city',       label: 'City',       field: 'city',     drillType: 'city' },
    { id: 'country',    label: 'Country',    field: 'country',  drillType: 'country' },
    { id: 'company',    label: 'Company',    field: 'company',  drillType: 'company' },
    { id: 'state',      label: 'State',      field: 'state',    drillType: 'state' },
];

const RAG = { green: '#66BB6A', amber: '#FFCA28', red: '#FF5252' };

// The dropdown shows the score-driver name (e.g. "Stage depth"); the value
// column instead names the concrete thing the number measures, with its unit,
// so a reader doesn't have to know the scoring model to read the table.
const DRIVER_COLUMN = {
    'fail-rate':     'Tests failed (%)',
    'repeat':        'Repeat clicks (%)',
    'stage-depth':   '2nd stage clicks (%)',
    'training-debt': 'Outstanding courses (%)',
    'course-lag':    'Avg days to complete',
};

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// RAG against the factor anchor. Every current factor is "lower is better",
// but the goodBelow check keeps this correct if that ever changes.
function ragFor(anchor, value) {
    if (!anchor) return null;
    const goodBelow = anchor.goodBelow !== false;
    // Amber-band reference: industry (no platform data source) else the real
    // platform anchor, falling back to the product target only if neither
    // exists — never an invented multiple.
    const ref = anchor.industry ?? anchor.platform ?? anchor.target;
    if (goodBelow) {
        if (value <= anchor.target) return 'green';
        if (value <= ref) return 'amber';
        return 'red';
    }
    if (value >= anchor.target) return 'green';
    if (value >= ref) return 'amber';
    return 'red';
}

// Toolbar select, shared shape with HighRiskUsersPage. Options may be plain
// strings or { value, label } pairs; omitting `placeholder` drops the empty
// option (used for the always-set driver / dimension selectors).
function FilterSelect({ value, onChange, options, placeholder, ariaLabel }) {
    const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
    return (
        <Box
            component="select"
            value={value}
            aria-label={ariaLabel}
            onChange={(e) => onChange(e.target.value)}
            sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: '4px',
                px: 1.25, py: '6px', fontSize: 11, color: value ? 'text.primary' : 'text.disabled',
                bgcolor: 'background.paper', outline: 'none', cursor: 'pointer', fontFamily: 'inherit',
                '&:focus': { borderColor: 'secondary.main' },
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='5' viewBox='0 0 8 5' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l3 3 3-3' stroke='%236B7591' stroke-width='1.2' stroke-linecap='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                pr: 3,
            }}
        >
            {placeholder && <option value="">{placeholder}</option>}
            {opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Box>
    );
}

// Search box, matched to the Users page toolbar input.
function SearchInput({ value, onChange, placeholder }) {
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 1, py: '3px',
            border: '1px solid', borderColor: 'divider', borderRadius: '4px',
            minWidth: 220,
            transition: 'border-color 150ms',
            '&:focus-within': { borderColor: 'secondary.main' },
        }}>
            <SearchIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Box
                component="input"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                sx={{
                    flex: 1, minWidth: 0,
                    border: 'none', outline: 'none', background: 'transparent',
                    color: 'text.primary', fontFamily: 'inherit', fontSize: 12,
                    '&::placeholder': { color: 'text.disabled' },
                }}
            />
            {value && (
                <Box
                    component="button"
                    type="button"
                    onClick={() => onChange('')}
                    aria-label="Clear search"
                    sx={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        border: 'none', background: 'transparent', cursor: 'pointer', color: 'text.disabled', p: 0,
                        '&:hover': { color: 'text.secondary' },
                        '&:focus-visible': { outline: '2px solid', outlineColor: 'secondary.main', borderRadius: '3px' },
                    }}
                >
                    <ClearIcon sx={{ fontSize: 14 }} />
                </Box>
            )}
        </Box>
    );
}

export default function DeptPage({ drill, scope }) {
    const [dimensionId, setDimensionId] = useState('department');
    // Tenancy-only slice forwarded to the server rollups: the leagues aggregate
    // reads the tenancy_dim_rollup directly (it never sees the client-pooled
    // ./data), so a Tenancy filter must be pushed through so the league pools
    // the selected child tenancies. Other dimensions stay pivots on this page.
    const tenancyScope = Array.isArray(scope?.tenancy) && scope.tenancy.length ? { tenancy: scope.tenancy } : undefined;
    const [driverId, setDriverId] = useState(SIGNALS[0].id);
    const [query, setQuery] = useState('');

    const dimension = DIMENSIONS.find((d) => d.id === dimensionId) || DIMENSIONS[0];
    const driver = SIGNALS.find((s) => s.id === driverId) || SIGNALS[0];

    // Server league: the leagues aggregate (tenancy_dim_rollup) carries the mean of every raw
    // risk signal per dimension value, computed over ALL scored users (the client model only
    // sees the 20k-capped roster). courseLag arrives in days; /30 matches the 0-1
    // normalised components so formatFactorValue renders identically.
    const leaguesQ = useDashboardData({ metric: 'simulations', aggregation: 'leagues', scope: tenancyScope });
    const benchQ = useRiskBenchmarkQuery(tenancyScope);
    const FACTOR_KEY = { 'fail-rate': 'failRate', repeat: 'repeat', 'stage-depth': 'stageDepth', 'training-debt': 'trainingDebt', 'course-lag': 'courseLag' };
    // RAG anchor: the constant target from SIGNALS, the platform / industry means from the server.
    const anchor = useMemo(() => {
        const bench = benchQ.data?.riskBenchmark;
        const field = FACTOR_KEY[driver.id];
        const suffix = field ? field.charAt(0).toUpperCase() + field.slice(1) : '';
        return {
            ...driver.anchor,
            industry: bench?.[`industry${suffix}`] ?? null,
            platform: bench?.[`platform${suffix}`] ?? null,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [driver, benchQ.data]);
    const league = useMemo(() => {
        const key = FACTOR_KEY[driver.id];
        const all = leaguesQ.data?.leagues;
        if (Array.isArray(all) && key) {
            const server = all
                .filter((r) => r.dimType === dimension.id && r[key] != null)
                .map((r) => ({ name: r.name, users: r.users, value: driver.id === 'course-lag' ? r.courseLag / 30 : r[key] }));
            const goodBelow = driver.anchor ? driver.anchor.goodBelow !== false : true;
            return server.sort((a, b) => (goodBelow ? b.value - a.value : a.value - b.value));
        }
        return [];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dimension, driver, leaguesQ.data]);

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return league;
        return league.filter((r) => r.name.toLowerCase().includes(q));
    }, [league, query]);

    // `name` column label tracks the dimension; the value column label and
    // formatting track the chosen score driver.
    const columns = [
        { id: 'name',  label: dimension.label, always: true,    sortable: true, type: 'string', accessor: (r) => r.name },
        { id: 'users', label: 'Users',         defaultOn: true, sortable: true, type: 'number', accessor: (r) => r.users, align: 'right' },
        { id: 'value', label: DRIVER_COLUMN[driver.id] || driver.label, defaultOn: true, sortable: true, type: 'number', accessor: (r) => r.value, align: 'right',
          format: (v) => formatFactorValue(driver, v),
          render: (r) => {
              const rag = ragFor(anchor, r.value);
              return <Box component="span" sx={{ fontWeight: 700, color: rag ? RAG[rag] : 'text.primary' }}>{formatFactorValue(driver, r.value)}</Box>;
          } },
    ];

    const dimLabel = pluralLabel(dimension.label).toLowerCase();

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <motion.div variants={fadeUp}>
                <WidgetState queries={[leaguesQ]}>
                <DataTable
                    title="League tables"
                    exportName={`league table ${dimension.label} ${driver.label}`}
                    subtitle={`${pluralLabel(dimension.label)} ranked by ${driver.label.toLowerCase()}, measured over the 6-month rolling window - click View to drill into simulations`}
                    columns={columns}
                    rows={rows}
                    rowKey={(r) => r.name}
                    onRowClick={(r) => drill && drill('sims', `Sims - ${r.name}`, { type: dimension.drillType, value: r.name })}
                    initialSort={{ by: 'value', dir: 'desc' }}
                    storageKey="rbx-league-cols"
                    emptyMessage={`No ${dimLabel} match the current filters`}
                    toolbar={
                        <>
                            <SearchInput value={query} onChange={setQuery} placeholder={`Search ${dimLabel}...`} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                                    Score driver
                                </Typography>
                                <FilterSelect
                                    value={driverId}
                                    onChange={setDriverId}
                                    options={SIGNALS.map((s) => ({ value: s.id, label: s.label }))}
                                    ariaLabel="Score driver"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
                                    Group by
                                </Typography>
                                <FilterSelect
                                    value={dimensionId}
                                    onChange={setDimensionId}
                                    options={DIMENSIONS.map((d) => ({ value: d.id, label: d.label }))}
                                    ariaLabel="Group by"
                                />
                            </Box>
                            {query && (
                                <Button size="small" onClick={() => setQuery('')}
                                    sx={{ fontSize: 10, color: 'text.disabled', textTransform: 'none', minWidth: 0, px: 1, '&:hover': { color: 'text.secondary' } }}>
                                    Clear
                                </Button>
                            )}
                        </>
                    }
                />
                </WidgetState>
            </motion.div>
        </motion.div>
    );
}
